/**
 * SPRINT V2 — Embajador Nacional.
 * Tracks per-user contribution to their chosen nation (predictions, hype,
 * shares, defenses) and derives a local "Embajador" tier. Pure client,
 * localStorage backed. No realtime, no backend.
 */
const K = "vs:v2:ambassador:v1";

export type NationalAmbassadorTier =
  | "fan"
  | "voz"
  | "capitan"
  | "leyenda";

export interface NationalAmbassadorState {
  nation: string | null;
  predictions: number;
  hype: number;
  shares: number;
  defenses: number;
  updatedAt: number;
}

const DEFAULT: NationalAmbassadorState = {
  nation: null,
  predictions: 0,
  hype: 0,
  shares: 0,
  defenses: 0,
  updatedAt: 0,
};

function read(): NationalAmbassadorState {
  if (typeof window === "undefined") return DEFAULT;
  try {
    const raw = window.localStorage.getItem(K);
    return raw ? { ...DEFAULT, ...(JSON.parse(raw) as NationalAmbassadorState) } : DEFAULT;
  } catch { return DEFAULT; }
}

function write(s: NationalAmbassadorState) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(K, JSON.stringify(s)); } catch { /* noop */ }
}

export function getAmbassadorState(nation: string | null): NationalAmbassadorState {
  const cur = read();
  if (nation && cur.nation !== nation) {
    const reset = { ...DEFAULT, nation, updatedAt: Date.now() };
    write(reset);
    return reset;
  }
  return cur;
}

export function bumpAmbassador(
  field: "predictions" | "hype" | "shares" | "defenses",
  delta = 1,
  nation?: string | null,
) {
  const cur = getAmbassadorState(nation ?? null);
  const next: NationalAmbassadorState = {
    ...cur,
    [field]: Math.max(0, cur[field] + delta),
    updatedAt: Date.now(),
  };
  write(next);
  return next;
}

export function ambassadorScore(s: NationalAmbassadorState): number {
  return Math.round(s.predictions * 4 + s.hype * 1 + s.shares * 6 + s.defenses * 8);
}

export function ambassadorTier(s: NationalAmbassadorState): NationalAmbassadorTier {
  const score = ambassadorScore(s);
  if (score >= 400) return "leyenda";
  if (score >= 180) return "capitan";
  if (score >= 60) return "voz";
  return "fan";
}

const TIER_INFO: Record<NationalAmbassadorTier, { label: string; emoji: string; glow: string; next: number | null }> = {
  fan:      { label: "Fan",              emoji: "🎽", glow: "#94a3b8", next: 60 },
  voz:      { label: "Voz Nacional",     emoji: "📣", glow: "#22d3ee", next: 180 },
  capitan:  { label: "Capitán Nacional", emoji: "🎖️", glow: "#facc15", next: 400 },
  leyenda:  { label: "Leyenda Nacional", emoji: "👑", glow: "#a855f7", next: null },
};

export function ambassadorTierInfo(tier: NationalAmbassadorTier) {
  return TIER_INFO[tier];
}

export function nextTierProgress(s: NationalAmbassadorState): { pct: number; remaining: number; nextThreshold: number | null } {
  const score = ambassadorScore(s);
  const tier = ambassadorTier(s);
  const next = TIER_INFO[tier].next;
  if (next == null) return { pct: 1, remaining: 0, nextThreshold: null };
  const prev = tier === "fan" ? 0 : tier === "voz" ? 60 : 180;
  const pct = Math.min(1, Math.max(0, (score - prev) / (next - prev)));
  return { pct, remaining: Math.max(0, next - score), nextThreshold: next };
}